import { ThemeOptions } from '../type';

/**
 * App-client dark palette tokens. Mirrors the light palette structure while aligning
 * with the shared dark theme scale from mloop-shared.
 */

/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
// eslint-disable-next-line @typescript-eslint/naming-convention
enum Color_DO_NOT_EXPORT {
  WHITE = 'rgba(255, 255, 255, 1)',
  BLACK = 'rgba(0, 0, 0, 1)',
  TRANSPARENT = 'rgba(0, 0, 0, 0)',

  GRAY_CONTRAST_MAX = 'rgba(255, 255, 255, 0.72)',
  GRAY_CONTRAST_HIGH = 'rgba(255, 255, 255, 0.57)',
  GRAY_CONTRAST_MID = 'rgba(255, 255, 255, 0.3)',
  GRAY_CONTRAST_LOW = 'rgba(255, 255, 255, 0.15)',
  GRAY_CONTRAST_MIN = 'rgba(255, 255, 255, 0.03)',

  SOLID_GRAY_CONTRAST_MAX = '#E5E8EC',
  SOLID_GRAY_CONTRAST_HIGH = '#C8CBD0',
  SOLID_GRAY_CONTRAST_MID = '#909399',
  SOLID_GRAY_CONTRAST_LOW = '#51545A',
  SOLID_GRAY_CONTRAST_MIN = '#2E3238',

  FOREGROUND_PRIMARY = '#F8F8F8',

  BACKGROUND_PRIMARY = '#1A1B24',
  BACKGROUND_ALTERNATE = '#1E1F29',
  BACKGROUND_CONTRAST = '#24252F',
  BACKGROUND_INFO = 'rgba(49, 147, 237, 0.16)',
  BACKGROUND_SUCCESS = 'rgba(13, 194, 173, 0.16)',
  BACKGROUND_WARNING = 'rgba(253, 215, 63, 0.16)',
  BACKGROUND_ERROR = 'rgba(231, 62, 54, 0.16)',
  BACKGROUND_HEADER = 'rgba(36, 37, 47, 0.5)',

  ACCENT = 'rgba(49, 147, 237, 0.2)',
  DISABLED = 'rgba(255, 255, 255, 0.3)',
  FOCUS = 'rgba(49, 147, 237, 0.45)',

  TEAL_LIGHT = '#0DC2AD',
  TEAL = '#08918D',
  TEAL_DARK = '#056667',

  RED_LIGHT = '#E73E36',
  RED = '#BC130C',
  RED_DARK = '#830303',

  YELLOW_LIGHT = '#FEE977',
  YELLOW = '#FDD73F',
  YELLOW_DARK = '#FABB00',

  BLUE_LIGHT = '#3193ED',
  BLUE = '#0E69BD',
  BLUE_DARK = '#034D91',

  PURPLE_LIGHT = 'rgba(109, 50, 234, 0.24)',
  PURPLE = '#6D32EA',
  PURPLE_DARK = '#481BB6',
  PURPLE_BACKGROUND = 'rgba(109, 50, 234, 0.16)',

  ORANGE_LIGHT = '#FFAA5B',
  ORANGE = '#EB600A',
  ORANGE_DARK = '#AD4200',
  ORANGE_BACKGROUND = 'rgba(235, 96, 10, 0.16)',
}

export const grey: ThemeOptions['grey'] = {
  alpha: {
    max: Color_DO_NOT_EXPORT.GRAY_CONTRAST_MAX,
    high: Color_DO_NOT_EXPORT.GRAY_CONTRAST_HIGH,
    mid: Color_DO_NOT_EXPORT.GRAY_CONTRAST_MID,
    low: Color_DO_NOT_EXPORT.GRAY_CONTRAST_LOW,
    min: Color_DO_NOT_EXPORT.GRAY_CONTRAST_MIN,
  },
  solid: {
    max: Color_DO_NOT_EXPORT.SOLID_GRAY_CONTRAST_MAX,
    high: Color_DO_NOT_EXPORT.SOLID_GRAY_CONTRAST_HIGH,
    mid: Color_DO_NOT_EXPORT.SOLID_GRAY_CONTRAST_MID,
    low: Color_DO_NOT_EXPORT.SOLID_GRAY_CONTRAST_LOW,
    min: Color_DO_NOT_EXPORT.SOLID_GRAY_CONTRAST_MIN,
  },
};

export const palette: ThemeOptions['palette'] = {
  mode: 'dark',
  common: {
    white: Color_DO_NOT_EXPORT.WHITE,
    black: Color_DO_NOT_EXPORT.BLACK,
    transparent: Color_DO_NOT_EXPORT.TRANSPARENT,
  },
  divider: Color_DO_NOT_EXPORT.GRAY_CONTRAST_LOW,
  border: Color_DO_NOT_EXPORT.GRAY_CONTRAST_LOW,
  borderState: {
    focus: Color_DO_NOT_EXPORT.FOCUS,
    focusShadow: Color_DO_NOT_EXPORT.FOCUS,
    error: Color_DO_NOT_EXPORT.RED,
    default: Color_DO_NOT_EXPORT.GRAY_CONTRAST_LOW,
    subtle: Color_DO_NOT_EXPORT.SOLID_GRAY_CONTRAST_MIN,
  },
  link: Color_DO_NOT_EXPORT.BLUE_LIGHT,
  text: {
    primary: Color_DO_NOT_EXPORT.FOREGROUND_PRIMARY,
    secondary: Color_DO_NOT_EXPORT.GRAY_CONTRAST_MAX,
    disabled: Color_DO_NOT_EXPORT.DISABLED,
  },
  action: {
    focus: Color_DO_NOT_EXPORT.FOCUS,
    selected: Color_DO_NOT_EXPORT.ACCENT,
    hover: Color_DO_NOT_EXPORT.GRAY_CONTRAST_MIN,
    disabled: Color_DO_NOT_EXPORT.GRAY_CONTRAST_LOW,
  },
  background: {
    default: Color_DO_NOT_EXPORT.BACKGROUND_PRIMARY,
    paper: Color_DO_NOT_EXPORT.BACKGROUND_PRIMARY,
    alternate: Color_DO_NOT_EXPORT.BACKGROUND_ALTERNATE,
    contrast: Color_DO_NOT_EXPORT.BACKGROUND_CONTRAST,
    header: Color_DO_NOT_EXPORT.BACKGROUND_HEADER,
    info: Color_DO_NOT_EXPORT.BACKGROUND_INFO,
    success: Color_DO_NOT_EXPORT.BACKGROUND_SUCCESS,
    warning: Color_DO_NOT_EXPORT.BACKGROUND_WARNING,
    error: Color_DO_NOT_EXPORT.BACKGROUND_ERROR,
    primary: Color_DO_NOT_EXPORT.BACKGROUND_INFO,
    secondary: Color_DO_NOT_EXPORT.BACKGROUND_SUCCESS,
    tertiary: Color_DO_NOT_EXPORT.PURPLE_BACKGROUND,
    pending: Color_DO_NOT_EXPORT.ORANGE_BACKGROUND,
  },
  primary: {
    light: Color_DO_NOT_EXPORT.BLUE_LIGHT,
    main: Color_DO_NOT_EXPORT.BLUE,
    dark: Color_DO_NOT_EXPORT.BLUE_DARK,
    contrastText: Color_DO_NOT_EXPORT.WHITE,
  },
  secondary: {
    light: Color_DO_NOT_EXPORT.TEAL_LIGHT,
    main: Color_DO_NOT_EXPORT.TEAL,
    dark: Color_DO_NOT_EXPORT.TEAL_DARK,
    contrastText: Color_DO_NOT_EXPORT.WHITE,
  },
  tertiary: {
    light: Color_DO_NOT_EXPORT.PURPLE_LIGHT,
    main: Color_DO_NOT_EXPORT.PURPLE,
    dark: Color_DO_NOT_EXPORT.PURPLE_DARK,
    contrastText: Color_DO_NOT_EXPORT.WHITE,
  },
  success: {
    light: Color_DO_NOT_EXPORT.TEAL_LIGHT,
    main: Color_DO_NOT_EXPORT.TEAL,
    dark: Color_DO_NOT_EXPORT.TEAL_DARK,
    contrastText: Color_DO_NOT_EXPORT.WHITE,
  },
  info: {
    light: Color_DO_NOT_EXPORT.BLUE_LIGHT,
    main: Color_DO_NOT_EXPORT.BLUE,
    dark: Color_DO_NOT_EXPORT.BLUE_DARK,
    contrastText: Color_DO_NOT_EXPORT.WHITE,
  },
  warning: {
    light: Color_DO_NOT_EXPORT.YELLOW_LIGHT,
    main: Color_DO_NOT_EXPORT.YELLOW,
    dark: Color_DO_NOT_EXPORT.YELLOW_DARK,
    contrastText: Color_DO_NOT_EXPORT.BLACK,
  },
  error: {
    light: Color_DO_NOT_EXPORT.RED_LIGHT,
    main: Color_DO_NOT_EXPORT.RED,
    dark: Color_DO_NOT_EXPORT.RED_DARK,
    contrastText: Color_DO_NOT_EXPORT.WHITE,
  },
  pending: {
    light: Color_DO_NOT_EXPORT.ORANGE_LIGHT,
    main: Color_DO_NOT_EXPORT.ORANGE,
    dark: Color_DO_NOT_EXPORT.ORANGE_DARK,
    contrastText: Color_DO_NOT_EXPORT.WHITE,
  },
};
